import React, { useState, useEffect } from 'react';
import { locationsAPI } from '../../../services/api';
import ImageUploader from '../Shared/ImageUploader';
import '../Shared/FormStyles.scss';

const LocationsManager = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    image: '',
    facilityName: '',
    aboutText: '',
    address: {
      street: '',
      city: '',
      state: '',
      postcode: '',
      country: 'Australia',
      fullAddress: '',
    },
    mapEmbedUrl: '',
    phone: '',
    email: '',
    openingHours: [
      { day: 'Monday', hours: '', isClosed: false },
      { day: 'Tuesday', hours: '', isClosed: false },
      { day: 'Wednesday', hours: '', isClosed: false },
      { day: 'Thursday', hours: '', isClosed: false },
      { day: 'Friday', hours: '', isClosed: false },
      { day: 'Saturday', hours: '', isClosed: false },
      { day: 'Sunday', hours: '', isClosed: true },
    ],
    order: 0,
    isActive: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await locationsAPI.getAllAdmin();
      setLocations(response.data);
    } catch (err) {
      setError('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [field]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleHourChange = (index, field, value) => {
    const newHours = [...formData.openingHours];
    newHours[index] = { ...newHours[index], [field]: value };
    setFormData({ ...formData, openingHours: newHours });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Ensure numeric fields are numbers, not strings
      const submitData = {
        ...formData,
        order: parseInt(formData.order, 10) || 0,
      };

      if (editing) {
        await locationsAPI.update(editing._id, submitData);
        setSuccess('Location updated successfully');
      } else {
        await locationsAPI.create(submitData);
        setSuccess('Location created successfully');
      }
      resetForm();
      fetchLocations();
    } catch (err) {
      console.error('Location save error:', err);
      
      let errorMessage = 'Failed to save location';
      
      if (err.code === 'ERR_NETWORK' || !err.response) {
        errorMessage = 'Network Error: Cannot connect to backend server. Please ensure the backend is running on port 5001.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('Full error details:', {
        message: err.message,
        code: err.code,
        response: err.response?.data,
        status: err.response?.status,
      });
    }
  };

  const handleEdit = (location) => {
    setEditing(location);
    setFormData({
      name: location.name || '',
      image: location.image || '',
      facilityName: location.facilityName || '',
      aboutText: location.aboutText || '',
      address: location.address || {
        street: '',
        city: '',
        state: '',
        postcode: '',
        country: 'Australia',
        fullAddress: '',
      },
      mapEmbedUrl: location.mapEmbedUrl || '',
      phone: location.phone || '',
      email: location.email || '',
      openingHours: location.openingHours || formData.openingHours,
      order: location.order || 0,
      isActive: location.isActive !== undefined ? location.isActive : true,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) {
      return;
    }

    try {
      await locationsAPI.delete(id);
      setSuccess('Location deleted successfully');
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete location');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      name: '',
      image: '',
      facilityName: '',
      aboutText: '',
      address: {
        street: '',
        city: '',
        state: '',
        postcode: '',
        country: 'Australia',
        fullAddress: '',
      },
      mapEmbedUrl: '',
      phone: '',
      email: '',
      openingHours: formData.openingHours,
      order: 0,
      isActive: true,
    });
  };
const timeSlots = [
  "7:00 AM - 3:00 PM",
  "8:00 AM - 4:00 PM",
  "9:00 AM - 5:00 PM",
  "10:00 AM - 6:00 PM",
  "11:00 AM - 7:00 PM",
  "Closed"
];

  if (loading) return <div>Loading...</div>;

  return (
    <div className="locations-manager">
      <div className="admin-form">
        <h2>{editing ? 'Edit Location' : 'Add New Location'}</h2>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
    
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
</div>

          <h3>Location Page</h3>
          <p style={{ fontSize: '13px', color: '#888', marginTop: '-8px', marginBottom: '16px' }}>
            These fields populate this location's own public page (e.g. "Our {formData.name || '[Name]'} clinic is located within {formData.facilityName || '[Facility Name]'}").
          </p>
          <div className="form-grid">
            <div className="form-group">
              <label>Facility / Building Name</label>
              <input
                type="text"
                name="facilityName"
                value={formData.facilityName}
                onChange={handleChange}
                placeholder="e.g. Aveley Medical Centre"
              />
            </div>
          </div>
          <ImageUploader
            value={formData.image}
            onChange={(url) => setFormData((prev) => ({ ...prev, image: url }))}
            label="Clinic Photo"
          />
          <div className="form-group">
            <label>About / Parking &amp; Access Info</label>
            <textarea
              name="aboutText"
              value={formData.aboutText}
              onChange={handleChange}
              rows={3}
              placeholder="e.g. Plenty of free accessible parking is available at the rear of the clinic."
            />
          </div>
          <p style={{ fontSize: '13px', color: '#888', margin: '0 0 16px' }}>
            Which services and team members appear on this location's page is controlled from the
            <strong> Services</strong> and <strong> Our Team</strong> admin pages — tick this location in their
            "Locations" checklist there.
          </p>

          <h3>Address</h3>
          <div className="form-grid">
          <div className="form-group">
            <label>Street</label>
            <input
              type="text"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
              />
            </div></div>
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                name="address.state"
                value={formData.address.state}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Postcode</label>
              <input
                type="text"
                name="address.postcode"
                value={formData.address.postcode}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Full Address (for display)</label>
            <textarea
              name="address.fullAddress"
              value={formData.address.fullAddress}
              onChange={handleChange}
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>Map Embed URL</label>
            <input
              type="url"
              name="mapEmbedUrl"
              value={formData.mapEmbedUrl}
              onChange={handleChange}
              placeholder="https://www.google.com/maps/embed?pb=..."
            />
          </div>

          <div className="form-row">
            <div className="form-grid">
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div></div>
          </div>

          <h3>Opening Hours</h3>
    <div className="hours-container">
  {formData.openingHours.map((hour, index) => (
    <div key={index} className="hour-row">
      <label className="day-label">{hour.day}</label>

      <label className="open-toggle">
        <input
          type="checkbox"
          checked={!hour.isClosed}
          onChange={(e) => handleHourChange(index, 'isClosed', !e.target.checked)}
        />
        Open
      </label>

      <select
        disabled={hour.isClosed}
        value={hour.hours}
        onChange={(e) => handleHourChange(index, 'hours', e.target.value)}
      >
        <option value="">Select hours</option>
        {timeSlots.map((slot, i) => (
          <option key={i} value={slot}>{slot}</option>
        ))}
      </select>
    </div>
  ))}
</div>

          <div className="form-row">
            <div className="form-group">
              <label>Order</label>
              <input
                type="number"
                name="order"
                value={formData.order}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                Active
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="dashboard-btn btn-primary">
              {editing ? 'Update Location' : 'Create Location'}
            </button>
            {editing && (
              <button type="button" className="dashboard-btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="data-table" style={{ marginTop: '30px' }}>
        <h2 style={{ padding: '20px', margin: 0 }}>All Locations</h2>
        {locations.length === 0 ? (
          <div className="empty-state">No locations found</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>Phone</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((location) => (
                <tr key={location._id}>
                  <td>{location.name}</td>
                  <td>{location.address?.fullAddress || 'N/A'}</td>
                  <td>{location.phone || 'N/A'}</td>
                  <td>{location.order}</td>
                  <td>{location.isActive ? 'Active' : 'Inactive'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-edit" onClick={() => handleEdit(location)}>
                        Edit
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(location._id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default LocationsManager;
